/* eslint-disable react/no-children-prop */
import React, { useEffect, useMemo, useState } from 'react';
import ModalWrapper from 'Common/Components/ModalWrapper';
import get from 'lodash/get';
import {
  ModalTitle, Title, BodyText, ModalContent, ButtonsContainer,
  ConfirmButton, CancelButton, Warning, ModalBody, BoldText,
  ImpactSummary, ImpactSection, ImpactSectionTitle, ImpactList,
  ImpactItem, ImpactName, ImpactCount, CountingRow
} from './styled';
import useKeyPress from 'Hooks/useKeyPress';
import SpinnerComponent from 'Common/Components/SpinnerContainer';
import api from 'services/api';
import { useSelector } from 'react-redux';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import { getDeleteImpact, getVulnId } from 'store/Manage/deleteImpact';

const MAX_ITEMS_TO_SHOW = 5;

const getPositiveInteger = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return null;
  return Math.trunc(parsed);
};

const getFetchedGroupCount = (vuln) => getPositiveInteger(get(vuln, 'group_count', get(vuln, 'value.group_count', null)));

const DeleteConfirmationModal = ({
  showDeleteConfirmation, selectAll, vulnsSelected,
  deleteVulns, hideModalDelete, vulnsCount, isFetching, entity
}) => {
  const workspaceSelected = useSelector(selectCurrentWorkspace);
  const [groupCountOverrides, setGroupCountOverrides] = useState({});
  const [isCountingImpact, setIsCountingImpact] = useState(false);
  const selectedSignature = useMemo(() => (
    vulnsSelected.map((vuln) => {
      const id = getVulnId(vuln);
      return [
        id,
        get(vuln, 'is_main', get(vuln, 'value.is_main', false)),
        get(vuln, 'group_count', get(vuln, 'value.group_count', ''))
      ].join(':');
    }).join('|')
  ), [vulnsSelected]);
  const deleteImpact = useMemo(() => (
    selectAll
      ? null
      : getDeleteImpact(vulnsSelected, { groupCountOverrides })
  ), [selectAll, groupCountOverrides, vulnsSelected]);
  const missingGroupIds = useMemo(() => (
    deleteImpact?.unknownGroups.map((group) => group.id).join('|') || ''
  ), [deleteImpact]);
  const count = selectAll ? vulnsCount : deleteImpact.total;
  const isGroupedDeletion = !selectAll && deleteImpact.hasGroupedDeletion;
  const isBulk = vulnsSelected.length > 1;
  const countTitle = count === 1 ? 'Vulnerability' : 'Vulnerabilities';
  const hasDuplicates = vulnsSelected.some((v) => get(v, 'is_main', get(v, 'value.is_main', false)));
  const disableDelete = isFetching || isCountingImpact;

  const bulk = `You're deleting ${count} Vulnerabilities${hasDuplicates ? ', ' : ''}`;
  const single = `A vulnerability will be deleted${hasDuplicates ? ', ' : ''}`;
  const confirmationText = '. This action cannot be undone. Are you sure you want to proceed?';
  const confirmationGroupText = `You're about to delete ${count} Vulnerabilities. This action can't be undone. Are you sure you want to proceed?`;

  useEffect(() => {
    setGroupCountOverrides({});
  }, [selectedSignature]);

  useEffect(() => {
    if (!showDeleteConfirmation || selectAll || !missingGroupIds) {
      setIsCountingImpact(false);
      return undefined;
    }

    let isMounted = true;
    const ids = missingGroupIds.split('|').filter(Boolean);

    const fetchGroupCounts = async () => {
      setIsCountingImpact(true);

      const countEntries = await Promise.all(ids.map(async (id) => {
        try {
          if (entity !== 'vulnsGeneral' && !workspaceSelected) return [id, 1];

          const vuln = entity === 'vulnsGeneral'
            ? await api.vulnsGeneral.fetchVulnById(id)
            : await api.manage.fetchById(workspaceSelected, id);

          return [id, getFetchedGroupCount(vuln) || 1];
        } catch (e) {
          return [id, 1];
        }
      }));

      if (!isMounted) return;
      const counts = countEntries.reduce((acc, [id, count]) => ({
        ...acc,
        [id]: count
      }), {});
      setGroupCountOverrides((current) => ({
        ...current,
        ...counts
      }));
      setIsCountingImpact(false);
    };

    fetchGroupCounts();

    return () => {
      isMounted = false;
    };
  }, [entity, missingGroupIds, selectAll, showDeleteConfirmation, workspaceSelected]);

  const normalBodyText = (
    <>
      <span children={ isBulk ? bulk : single } />
      <BoldText children={ hasDuplicates ? `including ${isBulk ? 'their' : 'its'} duplicates` : '' } />
      <span children={ confirmationText } />
    </>
  );
  let bodyContent = normalBodyText;
  if (isCountingImpact) {
    bodyContent = <span children="Checking how many vulnerabilities will be deleted. This can take a moment." />;
  } else if (isGroupedDeletion) {
    bodyContent = <span children={ confirmationGroupText } />;
  }

  const onCancel = () => hideModalDelete();
  const onDelete = () => {
    if (disableDelete) return;
    deleteVulns();
  };

  useKeyPress(() => {
    onCancel();
  }, ['Escape'], showDeleteConfirmation);

  useKeyPress(() => {
    onDelete();
  }, ['Enter'], showDeleteConfirmation && !disableDelete);

  if (!showDeleteConfirmation) return null;
  return (
    <ModalWrapper>
      <ModalContent>
        <ModalTitle>
          <Warning />
          <Title>{`Delete ${countTitle}`}</Title>
        </ModalTitle>
        <ModalBody>
          <BodyText>
            {bodyContent}
          </BodyText>
          {isCountingImpact && (
            <CountingRow>
              <SpinnerComponent height="18px" size="14px" />
              <span>Calculating vulnerabilities to delete...</span>
            </CountingRow>
          )}
          {!selectAll && isGroupedDeletion && !isCountingImpact && (
            <ImpactSummary>
              {deleteImpact.groups.length > 0 && (
                <ImpactSection>
                  <ImpactSectionTitle>Main vulnerabilities selected</ImpactSectionTitle>
                  <ImpactList>
                    {deleteImpact.groups.slice(0, MAX_ITEMS_TO_SHOW).map((group) => (
                      <ImpactItem key={ group.id }>
                        <ImpactName title={ group.name }>{group.name}</ImpactName>
                        <ImpactCount>{group.count} to delete</ImpactCount>
                      </ImpactItem>
                    ))}
                    {deleteImpact.groups.length > MAX_ITEMS_TO_SHOW && (
                      <ImpactItem>
                        <ImpactName>{`and ${deleteImpact.groups.length - MAX_ITEMS_TO_SHOW} more`}</ImpactName>
                      </ImpactItem>
                    )}
                  </ImpactList>
                </ImpactSection>
              )}
              {deleteImpact.individuals.length > 0 && (
                <ImpactSection>
                  <ImpactSectionTitle>Individually selected vulnerabilities</ImpactSectionTitle>
                  <ImpactList>
                    {deleteImpact.individuals.slice(0, MAX_ITEMS_TO_SHOW).map((vuln) => (
                      <ImpactItem key={ vuln.id }>
                        <ImpactName title={ vuln.name }>{vuln.name}</ImpactName>
                        <ImpactCount>1 to delete</ImpactCount>
                      </ImpactItem>
                    ))}
                    {deleteImpact.individuals.length > MAX_ITEMS_TO_SHOW && (
                      <ImpactItem>
                        <ImpactName>{`and ${deleteImpact.individuals.length - MAX_ITEMS_TO_SHOW} more`}</ImpactName>
                      </ImpactItem>
                    )}
                  </ImpactList>
                </ImpactSection>
              )}
            </ImpactSummary>
          )}
        </ModalBody>
        <ButtonsContainer>
          <CancelButton onClick={ onCancel }>
            Cancel
          </CancelButton>
          <ConfirmButton onClick={ onDelete } disabled={ disableDelete } $isLoading={ isCountingImpact }>
            Delete
          </ConfirmButton>
        </ButtonsContainer>
      </ModalContent>
    </ModalWrapper>
  );
};

export default DeleteConfirmationModal;
